/**
 * Utilitinės funkcijos darbui su localStorage - skirtos išvengti QuotaExceededError klaidos
 */

// Maksimalus localStorage dydis (baitais)
const MAX_STORAGE_SIZE = 4.5 * 1024 * 1024; // 4.5 MB yra saugi riba

/**
 * Patikrina localStorage užimtą vietą
 * @returns {number} - užimta vieta baitais
 */
export const getLocalStorageSize = () => {
  let size = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      size += (key.length + localStorage[key].length) * 2; // UTF-16 kiekvienas simbolis = 2 baitai
    }
  }
  return size;
};

/**
 * Ar localStorage artėja prie užpildymo limito
 * @returns {boolean}
 */
export const isLocalStorageNearlyFull = () => {
  const usedSize = getLocalStorageSize();
  const percentUsed = (usedSize / MAX_STORAGE_SIZE) * 100;
  return percentUsed > 80; // Jei užpildyta daugiau nei 80%
};

/**
 * Išvalo localStorage pagal pasirinktą strategiją
 * @param {string} strategy - išvalymo strategija: 'all' - viską, 'supabase' - tik supabase įrašus, 'old' - senus įrašus
 * @returns {number} - išvalytų įrašų skaičius
 */
export const cleanupLocalStorage = (strategy = 'old') => {
  let cleanedCount = 0;
  
  if (strategy === 'all') {
    // Išvalyti visus įrašus
    localStorage.clear();
    return 1; // Tiesiog pažymime, kad išvalyta
  }
  
  // Laikomų lokalių įrašų galiojimo laikas (14 dienų)
  const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  
  for (let key in localStorage) {
    if (!localStorage.hasOwnProperty(key)) continue;
    
    try {
      // Išvalo tik Supabase įrašus
      if (strategy === 'supabase' && key.startsWith('sb-')) {
        localStorage.removeItem(key);
        cleanedCount++;
        continue;
      }
      
      // Pabandyti ištraukti įrašo sukūrimo datą
      let item;
      try {
        item = JSON.parse(localStorage[key]);
      } catch (e) {
        // Jei ne JSON formatas, praleisti
        continue;
      }
      
      // Jei tai laikinas įrašas su timestamp lauku, patikrinti jo amžių
      if (strategy === 'old' && item && item.timestamp) {
        const age = now - item.timestamp;
        if (age > MAX_AGE_MS) {
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
    } catch (e) {
      // Tyliai praleisti klaidas
    }
  }
  
  return cleanedCount;
};

/**
 * Saugiai įrašo duomenis į localStorage, o jei nepavyksta - išvalo dalį cache ir bando dar kartą
 * @param {string} key - įrašo raktas
 * @param {string} value - reikšmė
 * @returns {boolean} - ar pavyko įrašyti
 */
export const safelyWriteToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (error instanceof DOMException && 
       (error.name === 'QuotaExceededError' || 
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
      
      console.warn(`LocalStorage quota exceeded when trying to write key "${key}". Attempting to clean up old entries.`);
      // Pabandyti išvalyti senus įrašus ir bandyti dar kartą
      const cleaned = cleanupLocalStorage('old');
      
      if (cleaned > 0) {
        try {
          localStorage.setItem(key, value);
          return true;
        } catch (e) {
          // Jei vis tiek nepavyko po seno valymo, registruojame klaidą, bet NEBEVALOME Supabase raktų.
          console.error(`Failed to write key "${key}" even after cleaning old entries. LocalStorage might be full. Error:`, e);
          return false;
        }
      } else {
        // Jei neradome senų įrašų išvalyti, registruojame klaidą. NEBEVALOME Supabase raktų.
        console.error(`Failed to write key "${key}". No old entries found to clean up. LocalStorage might be full.`);
        return false;
      }
    } else {
      // Kita klaida, ne quota
      console.error(`Failed to write key "${key}" to localStorage due to unexpected error:`, error);
      return false;
    }
  }
};
